import { TARGET_PERIODS } from 'features/challenge/config';
import {
  ChallengeTargetAmountTypeNullable,
  TargetPeriodNullable,
} from 'features/challenge/config/types';
import { useChallengeUnits } from 'features/challenge/hooks/useChallengeUnits';

type FormatOptions = {
  periodSeparator?: string; // Default: 'per'
  unitSeparator?: string; // Default: '/'
  valueUnitSeparator?: string; // Default: ' ' (space)
  formatter?: (value: number) => string; // Custom formatter for the value
};

export const useGetTargetText = () => {
  const { getUnitLabel } = useChallengeUnits();

  /**
   * Get the period text for a given target period
   */
  const getPeriodText = (targetPeriod: TargetPeriodNullable): string => {
    if (!targetPeriod) return '';

    const periodKey = targetPeriod.toUpperCase();
    if (periodKey === 'PER_DAY') {
      return TARGET_PERIODS.PER_DAY;
    } else if (periodKey === 'PER_WEEK') {
      return TARGET_PERIODS.PER_WEEK;
    } else if (periodKey === 'PER_SPRINT') {
      return TARGET_PERIODS.PER_SPRINT;
    }
    return '';
  };

  /**
   * Get the target text with customizable formatting
   */
  const getTargetText = (
    targetAmount: ChallengeTargetAmountTypeNullable,
    targetPeriod: TargetPeriodNullable,
    options?: FormatOptions
  ) => {
    if (!targetAmount?.unit) {
      return 'Select target';
    }

    const {
      periodSeparator = '/',
      unitSeparator = ' ',
      valueUnitSeparator = ' ',
      formatter = String,
    } = options || {};

    const unitLabel = getUnitLabel(
      targetAmount.unit,
      targetAmount.minValue ?? targetAmount.maxValue ?? targetAmount.value ?? 1
    );
    const periodText = getPeriodText(targetPeriod);

    const formatValue = (value: number) => {
      const formattedValue = formatter(value);
      return `${formattedValue}${valueUnitSeparator}${unitLabel}${unitSeparator}${periodSeparator}${unitSeparator}${periodText}`;
    };

    // Handle "avoid completely" case (maxValue === 0)
    if (targetAmount.maxValue === 0) {
      return 'avoid completely';
    }

    // If both min and max values exist and are equal, show "exactly X"
    if (
      targetAmount.minValue &&
      targetAmount.maxValue &&
      targetAmount.minValue === targetAmount.maxValue
    ) {
      return `exactly ${formatValue(targetAmount.minValue)}`;
    }

    // If both min and max values exist and are different, show range format
    if (
      targetAmount.minValue &&
      targetAmount.maxValue &&
      targetAmount.minValue !== targetAmount.maxValue
    ) {
      const minValueFormatted = formatter(targetAmount.minValue);
      const maxValueFormatted = formatter(targetAmount.maxValue);
      return `${minValueFormatted}-${maxValueFormatted}${valueUnitSeparator}${unitLabel}${unitSeparator}${periodSeparator}${unitSeparator}${periodText}`;
    }

    // If only min value exists, show just "minValue"
    if (targetAmount.minValue) {
      return `${formatValue(targetAmount.minValue)}`;
    }

    // If only max value exists, show "no more than maxValue"
    if (targetAmount.maxValue) {
      return `no more than ${formatValue(targetAmount.maxValue)}`;
    }

    // Fallback to original value
    if (targetAmount.value) {
      return formatValue(targetAmount.value);
    }

    return 'Select target';
  };

  /**
   * Get just the unit label for a given unit and count
   */
  const getUnitLabelOnly = (unit: string, count: number): string => {
    return getUnitLabel(unit, count);
  };

  /**
   * Get just the period text for a given target period
   */
  const getPeriodTextOnly = (targetPeriod: TargetPeriodNullable): string => {
    return getPeriodText(targetPeriod);
  };

  return {
    getTargetText,
    getUnitLabelOnly,
    getPeriodTextOnly,
  };
};
